import { exists } from 'i18next';
import { Asset, ASSET_TYPE, Edition, PageSection, ROW_TYPES } from '@24i/nxg-sdk-photon';
import { firstLetterUppercase } from '@24i/nxg-core-utils';

export const mapEditionToAssetTrailer = (edition: Edition, asset: Asset): Asset => {
    return {
        ...asset,
        editionId: edition.id,
        isTrailer: true,
    };
};

export const mapSearchSectionsLabels = (
    section: PageSection,
    t?: (label: string, { count }: { count: number }) => string
): PageSection => {
    let label = section.label || '';
    const itemsLength = section.items?.length || 0;

    if (t)
        switch (label) {
            case ASSET_TYPE.EPISODE:
                label = t('asset.series.episode.label', { count: itemsLength });
                break;
            case ASSET_TYPE.SERIES:
                label = t('asset.series.label', { count: itemsLength });
                break;
            case ASSET_TYPE.MOVIE:
                label = t('asset.movie', { count: itemsLength });
                break;
            case ASSET_TYPE.LIVE_EVENT:
                label = t('asset.liveEvent', { count: itemsLength });
                break;
            case ASSET_TYPE.CLIP:
                label = t('asset.clip', { count: itemsLength });
                break;
            default:
                if (exists(`asset.${label}.label`)) {
                    label = t(`asset.${label}.label`, { count: itemsLength });
                } else if (exists(`asset.${label}`)) {
                    label = t(`asset.${label}`, { count: itemsLength });
                }
                break;
        }

    if (section.items && section.items.length > 0 && label) label += ` (${section.items.length})`;

    return {
        ...section,
        label: firstLetterUppercase(label),
    };
};

/** Currently Backstage only supports a basic search functionality that returns all results in a single grid.
 *  In the future we want to return multiple rows of different content.
 *  This map function is temporary and it was created only to test multiple rows usecase */
export const mapSearchAssetsToSections = (
    initialItems: Asset[],
    t?: (label: string, { count }: { count: number }) => string
): PageSection[] => {
    const sections: { [sectionType: string]: PageSection } = {};

    for (let i = 0; i < initialItems.length; i++) {
        const currentAsset = initialItems[i];
        const { type = '' } = currentAsset;

        if (!sections[type]) {
            sections[type] = {
                id: type,
                display: 'landscape',
                label: type,
                items: [currentAsset],
                type: ROW_TYPES.SINGLE_ROW,
                // Temporary - I know itemLimit 9999 is not ideal, but backstage doesn't support
                // fetching /search assets with query asset_type. This way we can't get all items
                // when "show all" button from row in Web is clicked. As a workaround for now we'll show
                // all searched assets by type in a row but as mentioned above, in the future backstage should
                // support page sections for search items.
                itemLimit: 9999,
            };
        } else {
            sections[type].items = [...(sections[type].items || []), currentAsset];
        }
    }

    const mappedSections: PageSection[] = Object.values(sections).map((section) =>
        mapSearchSectionsLabels(section, t)
    );

    return mappedSections;
};
